import { useMemo } from "react";
import { BetSlipMode } from "../types/enums";

interface UseCalculateVatProps {
  selections: IBetSlipSelection[];
  config: ITaxCalculationConfig;
  stakes: { [key: string]: number };
  mode: BetSlipMode;
}

export const useCalculateVat = ({
  selections,
  config,
  stakes,
  mode,
}: UseCalculateVatProps): VatResult => {
  return useMemo(() => {
    const empty: VatResult = {
      exciseTaxValue: 0,
      singleExciseTax: 0,
      exciseTaxRate: 0,
      netStake: 0,
    };

    if (!selections.length || !config.taxes?.length) return empty;

    // Dominant product type: prematch=1, live=2, virtual=3
    // Tie-break offsets (0.2/0.1) match old-code behaviour: prematch > live > virtual
    const count = [0.2, 0.1, 0];
    selections.forEach((s) =>
      s.isVirtual ? count[2]++ : s.isLive ? count[1]++ : count[0]++,
    );
    const productTypeId = count.indexOf(Math.max(...count)) + 1;

    const taxEntry = config.taxes.find(
      (t) => t.ProductTypeId === productTypeId,
    );
    const exciseTaxRate = taxEntry?.ExciseTaxRate ?? 0;

    if (!exciseTaxRate) return empty;

    // Formula from old code: -(stake - stake / (1 + rate/100))
    // Result is negative (VAT is a deduction from gross stake)
    const calcTax = (stake: number) =>
      -(stake - stake / (1 + exciseTaxRate / 100));

    let exciseTaxValue = 0;
    let singleExciseTax = 0;
    let totalStake = 0;

    if (mode === BetSlipMode.Single) {
      selections.forEach((selection) => {
        const stake = stakes[selection.key] || 0;
        totalStake += stake;
        exciseTaxValue += calcTax(stake);
      });
      singleExciseTax = exciseTaxValue;
    } else {
      // combo
      const comboStake = stakes[BetSlipMode.Combo] || 0;
      totalStake = comboStake;
      singleExciseTax = calcTax(comboStake);
      exciseTaxValue = singleExciseTax;
    }

    const netStake = totalStake + exciseTaxValue; // exciseTaxValue is negative

    return {
      exciseTaxValue: Math.round(exciseTaxValue * 100) / 100,
      singleExciseTax: Math.round(singleExciseTax * 100) / 100,
      exciseTaxRate,
      netStake: Math.round(netStake * 100) / 100,
    };
  }, [selections, config, stakes, mode]);
};
